import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { EditorModule } from 'primeng/editor';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { ToolbarService } from '../../../services/toolbar.service';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { CrmQuotationJobType, CrmQuotationTermsTemplate } from '../../crm.model';
import { CrmService } from '../../crm.service';

@Component({
  selector: 'app-quotation-terms-config',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    BreadcrumbModule,
    ButtonModule,
    CheckboxModule,
    EditorModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageFormSectionComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  templateUrl: './quotation-terms-config.component.html',
  styleUrls: ['./quotation-terms-config.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuotationTermsConfigComponent implements OnInit {
  private crmService = inject(CrmService);
  private toolbarService = inject(ToolbarService);
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);

  templates = signal<CrmQuotationTermsTemplate[]>([]);
  loading = signal(false);
  homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };
  breadcrumbItems = [
    { label: 'CRM', routerLink: '/crm/quotations' },
    { label: 'Quotations', routerLink: '/crm/quotations' },
    { label: 'Templates' }
  ];

  jobTypeOptions = Object.values(CrmQuotationJobType)
    .map(value => ({ label: value.replaceAll('_', ' '), value }));

  form = this.fb.group({
    id: [null as number | null],
    companyId: [null as number | null],
    jobType: [CrmQuotationJobType.LOGISTICS, Validators.required],
    title: ['', Validators.required],
    description: [''],
    termsHtml: ['', Validators.required],
    active: [true]
  });

  ngOnInit() {
    this.form.patchValue({ companyId: this.toolbarService.customer()?.company?.id ?? null });
    this.load();
  }

  load() {
    this.loading.set(true);
    this.crmService.getQuotationTermsTemplates().subscribe({
      next: data => {
        this.templates.set(data || []);
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  edit(template: CrmQuotationTermsTemplate) {
    this.form.patchValue(template as any);
  }

  reset() {
    this.form.reset({
      companyId: this.toolbarService.customer()?.company?.id ?? null,
      jobType: CrmQuotationJobType.LOGISTICS,
      active: true
    });
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'There are validation errors. Please review the highlighted fields.' });
      return;
    }
    this.crmService.saveQuotationTermsTemplate(this.form.value as CrmQuotationTermsTemplate).subscribe(res => {
      if (res.success) {
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Quotation template saved.' });
        this.reset();
        this.load();
      }
    });
  }

  delete(template: CrmQuotationTermsTemplate) {
    if (!template.id) return;
    this.crmService.deleteQuotationTermsTemplate(template.id).subscribe(() => {
      this.templates.set(this.templates().filter(item => item.id !== template.id));
    });
  }
}
